import {
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import { useEffect, useRef, useState } from "react";

import Help from "~/components/common/Help";
import {
  CreateProviderTypeMutation,
  UpdateProviderTypeMutation,
} from "~/graphql/__generated__/graphql";
import { gql, useMutation } from "@apollo/client";
import { useOrgConfig, ProviderType } from "~/providers/OrgConfigProvider";

const TabProviderTypes = () => {
  const orgConfig = useOrgConfig();

  const [createMutation] =
    useMutation<CreateProviderTypeMutation>(CREATE_PROVIDER_TYPE, {
      refetchQueries: ["OrgConfig", "OrganizationProviderConfig"],
    });
  const [updateMutation] =
    useMutation<UpdateProviderTypeMutation>(UPDATE_PROVIDER_TYPE, {
      refetchQueries: ["OrgConfig", "OrganizationProviderConfig"],
    });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentProviderType, setCurrentProviderType] =
    useState<ProviderType | null>(null);

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const nameFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (dialogOpen) {
      setTimeout(() => {
        nameFieldRef.current?.focus();
      });
    }
  }, [dialogOpen]);

  const createProviderType = async (name: string) => {
    await createMutation({
      variables: {
        name,
      },
    });
  };

  const updateProviderTypeName = async (
    providerType: ProviderType,
    newValue: string
  ) => {
    await updateMutation({
      variables: {
        providerTypeId: providerType.id,
        name: newValue,
      },
      optimisticResponse: {
        updateProviderType: {
          ...providerType,
          name: newValue,
        },
      },
    });
  };

  const updateRequireStateLicense = async (providerType: ProviderType) => {
    const newValue = !providerType.requireStateLicense;
    await updateMutation({
      variables: {
        providerTypeId: providerType.id,
        requireStateLicense: newValue,
      },
      optimisticResponse: {
        updateProviderType: {
          ...providerType,
          requireStateLicense: newValue,
        },
      },
    });
  };

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                Provider Type
                <Help text="Provider types are various roles or titles that a provider can hold." />
              </TableCell>
              <TableCell>
                Requires State License?
                <Help text="If checked, the provider must have a license in the state where the patient resides to deliver care." />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.values(orgConfig.providerTypes).map((providerType) => (
              <TableRow key={providerType.id}>
                <TableCell>
                  {providerType.name}
                  <IconButton
                    size="small"
                    onClick={() => {
                      setCurrentProviderType(providerType);
                      setDialogOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell>
                  <Switch
                    checked={providerType.requireStateLicense}
                    onClick={() => updateRequireStateLicense(providerType)}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={3}>
                <Button
                  onClick={() => {
                    setCurrentProviderType(null);
                    setDialogOpen(true);
                  }}
                  size="small"
                  startIcon={<AddCircleOutlineIcon fontSize="small" />}
                >
                  Add New Provider Type
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Card>

      <Dialog
        fullWidth
        maxWidth="xs"
        open={dialogOpen}
        onClose={handleDialogClose}
        PaperProps={{
          component: "form",
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (currentProviderType) {
              updateProviderTypeName(
                currentProviderType,
                nameFieldRef.current?.value!
              );
            } else {
              createProviderType(nameFieldRef.current?.value!);
            }
            handleDialogClose();
          },
        }}
      >
        <DialogTitle>Provider Type Name</DialogTitle>
        <DialogContent>
          <TextField
            defaultValue={currentProviderType?.name || ""}
            autoFocus
            required
            margin="dense"
            label="Name (required)"
            fullWidth
            variant="standard"
            autoCorrect="off"
            inputRef={nameFieldRef}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button type="submit">
            {currentProviderType ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TabProviderTypes;

const CREATE_PROVIDER_TYPE = gql(`
  mutation CreateProviderType($name: String!) @permission(requires: manageOrgConfig) {
    createProviderType(name: $name) {
      id
      name
      requireStateLicense
    }
  }
`);

const UPDATE_PROVIDER_TYPE = gql(`
  mutation UpdateProviderType(
    $providerTypeId: Int!
    $name: String
    $requireStateLicense: Boolean
  )
  @permission(requires: manageOrgConfig)
  {
    updateProviderType(
      id: $providerTypeId
      name: $name
      requireStateLicense: $requireStateLicense
    ) {
      id
      name
      requireStateLicense
    }
  }
`);
